import { useState, useEffect } from 'react';
import {
  TrophyIcon,
  PlusIcon,
  TrashIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface SpecialPrize {
  id: string;
  tournament_id: string;
  round_number: number;
  prize_type: 'nearest_pin' | 'longest_drive' | 'hole_in_one';
  gender: 'men' | 'ladies';
  hole_number: number | null;
  winner_player_id: string | null;
  winner_name: string | null;
  distance: string | null;
  notes: string | null;
  created_at: string;
}

interface Round {
  id: string;
  round_number: number;
  name: string;
}

interface Player {
  id: string;
  first_name: string;
  last_name: string;
  gender?: string;
}

interface Props {
  tournamentId: string;
}

const PRIZE_LABELS: Record<string, string> = {
  nearest_pin: 'Nearest to the Pin',
  longest_drive: 'Longest Drive',
  hole_in_one: 'Hole-in-One',
};

const PRIZE_ICONS: Record<string, string> = {
  nearest_pin: '🎯',
  longest_drive: '💨',
  hole_in_one: '🏆',
};

const SETUP_SQL = `
CREATE TABLE IF NOT EXISTS special_prizes (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  tournament_id uuid NOT NULL REFERENCES tournaments(id) ON DELETE CASCADE,
  round_number integer NOT NULL,
  prize_type text NOT NULL CHECK (prize_type IN ('nearest_pin', 'longest_drive', 'hole_in_one')),
  gender text NOT NULL DEFAULT 'men' CHECK (gender IN ('men', 'ladies')),
  hole_number integer,
  winner_player_id uuid REFERENCES players(id) ON DELETE SET NULL,
  winner_name text,
  distance text,
  notes text,
  created_at timestamptz DEFAULT now(),
  updated_at timestamptz DEFAULT now()
);
ALTER TABLE special_prizes ENABLE ROW LEVEL SECURITY;
DO $$ BEGIN IF NOT EXISTS (SELECT 1 FROM pg_policies WHERE tablename='special_prizes' AND policyname='Anyone can view special prizes') THEN CREATE POLICY "Anyone can view special prizes" ON special_prizes FOR SELECT TO public USING (true); END IF; END $$;
DO $$ BEGIN IF NOT EXISTS (SELECT 1 FROM pg_policies WHERE tablename='special_prizes' AND policyname='Auth can insert special prizes') THEN CREATE POLICY "Auth can insert special prizes" ON special_prizes FOR INSERT TO authenticated WITH CHECK (true); END IF; END $$;
DO $$ BEGIN IF NOT EXISTS (SELECT 1 FROM pg_policies WHERE tablename='special_prizes' AND policyname='Auth can update special prizes') THEN CREATE POLICY "Auth can update special prizes" ON special_prizes FOR UPDATE TO authenticated USING (true) WITH CHECK (true); END IF; END $$;
DO $$ BEGIN IF NOT EXISTS (SELECT 1 FROM pg_policies WHERE tablename='special_prizes' AND policyname='Auth can delete special prizes') THEN CREATE POLICY "Auth can delete special prizes" ON special_prizes FOR DELETE TO authenticated USING (true); END IF; END $$;
`;

export default function SpecialPrizesManager({ tournamentId }: Props) {
  const [prizes, setPrizes] = useState<SpecialPrize[]>([]);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeRound, setActiveRound] = useState<number>(1);

  const [form, setForm] = useState({
    prize_type: 'nearest_pin' as 'nearest_pin' | 'longest_drive' | 'hole_in_one',
    gender: 'men' as 'men' | 'ladies',
    hole_number: '',
    winner_player_id: '',
    winner_name: '',
    distance: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  const setupTable = async () => {
    setSettingUp(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { toast.error('Not authenticated'); return; }

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/run-migration`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ sql: SETUP_SQL }),
        }
      );
      const result = await res.json();
      if (result.success) {
        toast.success('Special prizes table created');
        setNeedsSetup(false);
        fetchData();
      } else {
        toast.error('Setup failed: ' + (result.error || 'Unknown error'));
      }
    } catch (err: any) {
      toast.error(err.message || 'Setup failed');
    } finally {
      setSettingUp(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: roundsData } = await supabase
        .from('tournament_rounds')
        .select('id, round_number, name')
        .eq('tournament_id', tournamentId)
        .order('round_number');

      if (roundsData) setRounds(roundsData);

      const { data: playersData } = await supabase
        .from('players')
        .select('id, first_name, last_name, gender')
        .eq('tournament_id', tournamentId)
        .order('last_name');

      if (playersData) setPlayers(playersData);

      const { data: prizesData, error } = await supabase
        .from('special_prizes')
        .select('*')
        .eq('tournament_id', tournamentId)
        .order('round_number')
        .order('prize_type');

      if (error) {
        if (error.message?.includes('does not exist') || error.code === '42P01') {
          setNeedsSetup(true);
        } else {
          toast.error('Failed to load prizes');
        }
      } else {
        setPrizes(prizesData || []);
      }
    } catch {
      // Table might not exist yet
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.winner_name && !form.winner_player_id) {
      toast.error('Please select a player or enter a winner name');
      return;
    }
    setSubmitting(true);
    try {
      const selectedPlayer = form.winner_player_id
        ? players.find(p => p.id === form.winner_player_id)
        : null;

      const { error } = await supabase.from('special_prizes').insert({
        tournament_id: tournamentId,
        round_number: activeRound,
        prize_type: form.prize_type,
        gender: form.gender,
        hole_number: form.hole_number ? parseInt(form.hole_number) : null,
        winner_player_id: form.winner_player_id || null,
        winner_name: form.winner_player_id
          ? `${selectedPlayer?.first_name} ${selectedPlayer?.last_name}`
          : form.winner_name,
        distance: form.distance || null,
        notes: form.notes || null,
      });

      if (error) throw error;
      toast.success('Special prize recorded');
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save prize');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this prize record?')) return;
    try {
      const { error } = await supabase.from('special_prizes').delete().eq('id', id);
      if (error) throw error;
      toast.success('Prize deleted');
      fetchData();
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete');
    }
  };

  const resetForm = () => {
    setForm({
      prize_type: 'nearest_pin',
      gender: 'men',
      hole_number: '',
      winner_player_id: '',
      winner_name: '',
      distance: '',
      notes: '',
    });
  };

  const roundPrizes = prizes.filter(p => p.round_number === activeRound);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-golf-green border-t-transparent" />
      </div>
    );
  }

  if (needsSetup) {
    return (
      <div className="text-center py-12">
        <TrophyIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Special Prizes</h3>
        <p className="text-sm text-gray-500 mb-6">
          Set up the special prizes table to track Nearest to Pin, Longest Drive, and Hole-in-One winners.
        </p>
        <button
          onClick={setupTable}
          disabled={settingUp}
          className="px-6 py-2.5 bg-golf-green text-white rounded-lg font-medium hover:bg-green-800 transition-colors disabled:opacity-50"
        >
          {settingUp ? 'Setting up...' : 'Set Up Special Prizes'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Round selector */}
      {rounds.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {rounds.map(r => (
            <button
              key={r.id}
              onClick={() => setActiveRound(r.round_number)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeRound === r.round_number
                  ? 'bg-golf-green text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {r.name || `Round ${r.round_number}`}
            </button>
          ))}
        </div>
      )}

      {/* Prize categories for active round */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Nearest to Pin - Men */}
        {renderPrizeCard('nearest_pin', 'men', roundPrizes)}
        {/* Nearest to Pin - Ladies */}
        {renderPrizeCard('nearest_pin', 'ladies', roundPrizes)}
        {/* Longest Drive - Men */}
        {renderPrizeCard('longest_drive', 'men', roundPrizes)}
        {/* Longest Drive - Ladies */}
        {renderPrizeCard('longest_drive', 'ladies', roundPrizes)}
        {/* Hole-in-One (Championship round only) */}
        {renderPrizeCard('hole_in_one', 'men', roundPrizes)}
        {renderPrizeCard('hole_in_one', 'ladies', roundPrizes)}
      </div>

      {/* Add prize button */}
      <div className="flex justify-center pt-2">
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 px-5 py-2.5 bg-golf-green text-white rounded-lg font-medium hover:bg-green-800 transition-colors shadow-sm"
        >
          <PlusIcon className="w-4 h-4" />
          Record Special Prize
        </button>
      </div>

      {/* Add prize form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Record Special Prize - {rounds.find(r => r.round_number === activeRound)?.name || `Round ${activeRound}`}
            </h3>

            <div className="space-y-4">
              {/* Prize Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prize Type</label>
                <select
                  value={form.prize_type}
                  onChange={e => setForm({ ...form, prize_type: e.target.value as any })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-golf-green focus:border-transparent"
                >
                  <option value="nearest_pin">Nearest to the Pin</option>
                  <option value="longest_drive">Longest Drive</option>
                  <option value="hole_in_one">Hole-in-One</option>
                </select>
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, gender: 'men' })}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                      form.gender === 'men'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Men
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, gender: 'ladies' })}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                      form.gender === 'ladies'
                        ? 'bg-pink-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Ladies
                  </button>
                </div>
              </div>

              {/* Hole Number */}
              {(form.prize_type === 'nearest_pin' || form.prize_type === 'hole_in_one') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hole Number {form.prize_type === 'hole_in_one' && '(Hole 14)'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="18"
                    value={form.hole_number}
                    onChange={e => setForm({ ...form, hole_number: e.target.value })}
                    placeholder={form.prize_type === 'hole_in_one' ? '14' : 'Enter hole number'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-golf-green focus:border-transparent"
                  />
                </div>
              )}

              {/* Winner - Player select or manual name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Winner</label>
                <select
                  value={form.winner_player_id}
                  onChange={e => setForm({ ...form, winner_player_id: e.target.value, winner_name: '' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-golf-green focus:border-transparent"
                >
                  <option value="">-- Select Player --</option>
                  {players.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.first_name} {p.last_name}
                    </option>
                  ))}
                </select>
                {!form.winner_player_id && (
                  <input
                    type="text"
                    value={form.winner_name}
                    onChange={e => setForm({ ...form, winner_name: e.target.value })}
                    placeholder="Or type winner name manually"
                    className="w-full mt-2 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-golf-green focus:border-transparent"
                  />
                )}
              </div>

              {/* Distance */}
              {form.prize_type !== 'hole_in_one' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {form.prize_type === 'nearest_pin' ? 'Distance from Pin' : 'Drive Distance'}
                  </label>
                  <input
                    type="text"
                    value={form.distance}
                    onChange={e => setForm({ ...form, distance: e.target.value })}
                    placeholder={form.prize_type === 'nearest_pin' ? 'e.g. 1.2m' : 'e.g. 285 yards'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-golf-green focus:border-transparent"
                  />
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={form.notes}
                  onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="Any additional notes"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-golf-green focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowForm(false)}
                className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-2.5 bg-golf-green text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Save Prize'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function renderPrizeCard(
    type: 'nearest_pin' | 'longest_drive' | 'hole_in_one',
    gender: 'men' | 'ladies',
    roundPrizesArr: SpecialPrize[]
  ) {
    const matching = roundPrizesArr.filter(p => p.prize_type === type && p.gender === gender);
    const borderColor = gender === 'men' ? 'border-blue-200' : 'border-pink-200';
    const headerBg = gender === 'men' ? 'bg-blue-50' : 'bg-pink-50';
    const genderLabel = gender === 'men' ? 'Men' : 'Ladies';

    return (
      <div key={`${type}-${gender}`} className={`border ${borderColor} rounded-xl overflow-hidden`}>
        <div className={`${headerBg} px-4 py-2.5 flex items-center gap-2`}>
          <span className="text-lg">{PRIZE_ICONS[type]}</span>
          <div>
            <p className="text-sm font-semibold text-gray-800">{PRIZE_LABELS[type]}</p>
            <p className="text-xs text-gray-500">{genderLabel}</p>
          </div>
        </div>
        <div className="p-4">
          {matching.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No winner recorded yet</p>
          ) : (
            <div className="space-y-2">
              {matching.map(prize => (
                <div key={prize.id} className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {prize.winner_name || 'Unknown'}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {prize.hole_number && (
                        <span className="text-xs text-gray-500">
                          <FlagIcon className="w-3 h-3 inline mr-0.5" />
                          Hole {prize.hole_number}
                        </span>
                      )}
                      {prize.distance && (
                        <span className="text-xs text-gray-500">{prize.distance}</span>
                      )}
                    </div>
                    {prize.notes && (
                      <p className="text-xs text-gray-400 mt-0.5">{prize.notes}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(prize.id)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
}
